import Janus, { JanusJS } from 'janus-gateway';
import adapter from 'webrtc-adapter';

import {
  JanusConnection,
  JanusConnectionFactory,
  JanusJsep,
  JanusVideoRoomHandle,
} from './janusCallSession';

// Concrete janus.js implementation of the session's injected adapter
// interfaces (OI-1564). Bridges JanusJS types ↔ the library-agnostic session
// contract. Runtime is the browser/staging-hub spike boundary — not unit-tested
// (janus.js needs real WebRTC); the session orchestration is tested with a fake.

const VIDEOROOM_PLUGIN = 'janus.plugin.videoroom';

let initialized = false;
const ensureInit = (onReady: () => void): void => {
  if (initialized) {
    onReady();
    return;
  }
  Janus.init({
    debug: false,
    dependencies: Janus.useDefaultDependencies({ adapter }),
    callback: () => {
      initialized = true;
      onReady();
    },
  });
};

const toJsep = (j: JanusJS.JSEP): JanusJsep => ({
  type: j.type ?? '',
  sdp: j.sdp ?? '',
});

const wrapHandle = (h: JanusJS.PluginHandle): JanusVideoRoomHandle => ({
  send: (o) =>
    h.send({
      message: o.message as unknown as JanusJS.PluginMessage['message'],
      jsep: o.jsep,
    }),
  createOffer: (o) =>
    h.createOffer({
      media: { audio: o.media.audio, video: o.media.video },
      customizeSdp: (jsep) => {
        const local = toJsep(jsep);
        o.customizeSdp(local);
        jsep.sdp = local.sdp; // write the munged SDP back for janus.js
      },
      success: (jsep) => o.success(toJsep(jsep)),
      error: (err) => o.error(err),
    }),
  createAnswer: (o) =>
    h.createAnswer({
      jsep: o.jsep,
      media: { audioSend: o.media.audioSend, videoSend: o.media.videoSend },
      success: (jsep) => o.success(toJsep(jsep)),
      error: (err) => o.error(err),
    }),
  handleRemoteJsep: (o) => h.handleRemoteJsep({ jsep: o.jsep }),
  hangup: () => h.hangup(),
  detach: () => h.detach(),
});

export const janusConnect: JanusConnectionFactory = ({
  url,
  token,
  onConnected,
  onError,
}) => {
  ensureInit(() => {
    const janus = new Janus({
      server: url,
      token,
      withCredentials: true,
      success: () => {
        const conn: JanusConnection = {
          attachVideoRoom: (cb) => {
            const localStream = new MediaStream();
            janus.attach({
              plugin: VIDEOROOM_PLUGIN,
              success: (h) => cb.onAttached(wrapHandle(h)),
              onmessage: (message, jsep) =>
                cb.onMessage(
                  message as unknown as Record<string, unknown>,
                  jsep ? toJsep(jsep) : undefined,
                ),
              onlocaltrack: (track, on) => {
                if (on) localStream.addTrack(track);
                else localStream.removeTrack(track);
                cb.onLocalStream?.(localStream);
              },
              error: (e) => cb.onError(e),
            });
          },
          attachSubscriber: (cb) => {
            const remoteStream = new MediaStream();
            janus.attach({
              plugin: VIDEOROOM_PLUGIN,
              success: (h) => cb.onAttached(wrapHandle(h)),
              onmessage: (message, jsep) =>
                cb.onMessage(
                  message as unknown as Record<string, unknown>,
                  jsep ? toJsep(jsep) : undefined,
                ),
              onremotetrack: (track, _mid, on) => {
                if (on) remoteStream.addTrack(track);
                else remoteStream.removeTrack(track);
                cb.onRemoteStream?.(remoteStream);
              },
              error: (e) => cb.onError(e),
            });
          },
          destroy: () => janus.destroy({}),
        };
        onConnected(conn);
      },
      error: (e) => onError(e),
    });
  });
};
